import Sidebar from "./Sidebar";
import InstallPrompt from "./InstallPrompt";
import { Bell, Sparkles, ChevronDown, Menu, User, LifeBuoy, LogOut, Activity } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";

const pageTitles = {
  "/dashboard": { title: "Welcome back, {name}", sub: "Let's accelerate your career journey today." },
  "/upload-resume": { title: "Upload Your Resume", sub: "Let AI parse and optimize your career profile." },
  "/job-search": { title: "Find Your Next Role", sub: "Search across LinkedIn, Indeed, Glassdoor — powered by Apify." },
  "/job-matches": { title: "Top Job Matches", sub: "AI-ranked opportunities for your profile." },
  "/applications": { title: "Applications Tracker", sub: "Track every stage of your job hunt." },
  "/ats-optimizer": { title: "ATS Optimizer", sub: "Beat the bots — get your resume past Applicant Tracking Systems." },
  "/cover-letter": { title: "Cover Letter Generator", sub: "AI-crafted, role-specific cover letters." },
  "/interview-prep": { title: "Interview Prep", sub: "Practice with your AI interview coach." },
  "/auto-apply": { title: "Auto Apply", sub: "Set rules, approve queue — let your agent do the work." },
  "/connections": { title: "Your Network", sub: "Warm intros beat cold applications 4×. Manage your inside contacts." },
  "/salary-insights": { title: "Salary Insights", sub: "Know your market value — powered by Claude." },
  "/calendar": { title: "Calendar", sub: "Your interview schedule at a glance." },
  "/profile": { title: "Your Profile", sub: "Career details used by every AI agent." },
  "/subscribe": { title: "Plans", sub: "Choose the plan that fits your hunt." },
  "/tailored-resume": { title: "Tailored Resume", sub: "Paste a JD — get a job-specific, ATS-optimized version of your resume." },
};

function timeAgo(iso) {
  try {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

function HeaderMenus() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null); // 'bell' | 'user' | null
  const [activities, setActivities] = useState(null);

  const toggleBell = async () => {
    const next = open === "bell" ? null : "bell";
    setOpen(next);
    if (next === "bell" && activities === null) {
      try {
        const r = await api.get("/dashboard/activities");
        setActivities(r.data || []);
      } catch { setActivities([]); }
    }
  };

  const signOut = async () => {
    setOpen(null);
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex items-center gap-2 sm:gap-3 shrink-0 relative">
      {open && <div className="fixed inset-0 z-40" data-testid="menu-backdrop" onClick={() => setOpen(null)} />}
      <div className="jr-pill !hidden md:!inline-flex" style={{ background: "rgba(34,211,238,0.12)", color: "#67e8f9" }} data-testid="copilot-active-badge">
        <Sparkles className="w-3.5 h-3.5" />
        <span>AI Copilot Active</span>
      </div>

      <div className="relative z-50">
        <button className="jr-btn-ghost p-2" aria-label="notifications" data-testid="notifications-btn" onClick={toggleBell}>
          <Bell className="w-4 h-4" />
        </button>
        {open === "bell" && (
          <div className="absolute right-0 top-11 jr-card border border-violet-500/30 shadow-2xl shadow-black/50 z-50 p-3" style={{ maxWidth: "none", width: "min(320px, 85vw)" }} data-testid="notifications-panel">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 px-1 mb-2 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> Recent activity
            </div>
            {activities === null && <div className="text-sm text-zinc-500 px-1 py-3">Loading…</div>}
            {activities !== null && activities.length === 0 && (
              <div className="text-sm text-zinc-500 px-1 py-3" data-testid="no-notifications-msg">No activity yet — search jobs or upload a resume.</div>
            )}
            {(activities || []).slice(0, 6).map((a, i) => (
              <div key={a.created_at || i} className="flex items-start gap-2 px-1 py-2 border-t border-white/5 first:border-0">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-300 leading-snug">{a.text}</div>
                  <div className="text-[11px] text-zinc-500">{a.created_at ? timeAgo(a.created_at) : ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="relative z-50">
        <button className="flex items-center gap-2 jr-btn-ghost py-1.5 pr-2 pl-1" data-testid="user-menu" onClick={() => setOpen(open === "user" ? null : "user")}>
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-cyan-400 grid place-items-center text-white text-xs font-bold">
            {(user?.name || "U").slice(0, 2).toUpperCase()}
          </div>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open === "user" ? "rotate-180" : ""}`} />
        </button>
        {open === "user" && (
          <div className="absolute right-0 top-11 jr-card border border-violet-500/30 shadow-2xl shadow-black/50 z-50 p-2" style={{ maxWidth: "none", width: "min(240px, 85vw)" }} data-testid="user-menu-panel">
            <div className="px-3 py-2 border-b border-white/5 mb-1">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
            <Link to="/profile" onClick={() => setOpen(null)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-zinc-300 hover:bg-white/5 hover:text-white" data-testid="menu-profile-link">
              <User className="w-4 h-4" /> My Profile
            </Link>
            <Link to="/contact" onClick={() => setOpen(null)} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-zinc-300 hover:bg-white/5 hover:text-white" data-testid="menu-support-link">
              <LifeBuoy className="w-4 h-4" /> Customer Support
            </Link>
            <button onClick={signOut} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-rose-300 hover:bg-rose-500/10" data-testid="menu-logout-btn">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const key = Object.keys(pageTitles).find((k) => location.pathname.startsWith(k)) || "/dashboard";
  const meta = pageTitles[key];
  const title = meta.title.replace("{name}", user?.name?.split(" ")[0] || "there");

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  return (
    <div className="App flex" data-testid="app-root">
      <Sidebar mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
      {navOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" data-testid="mobile-nav-backdrop" onClick={() => setNavOpen(false)} />
      )}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="px-4 sm:px-8 pt-4 sm:pt-6 pb-4 flex items-start justify-between gap-3 sm:gap-6">
          <div className="flex items-start gap-3 min-w-0">
            <button
              className="jr-btn-ghost p-2 lg:hidden shrink-0 mt-0.5"
              aria-label="open menu"
              data-testid="mobile-menu-btn"
              onClick={() => setNavOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white truncate" data-testid="page-title">
                {title} <span className="inline-block">👋</span>
              </h1>
              <p className="text-zinc-400 mt-1 text-sm sm:text-base hidden sm:block" data-testid="page-subtitle">{meta.sub}</p>
            </div>
          </div>
          <HeaderMenus />
        </header>

        <div className="px-4 sm:px-8 pb-12 jr-fade-in" data-testid="page-content">
          <Outlet />
        </div>

        <footer className="px-4 sm:px-8 pb-6 text-xs text-zinc-500 flex flex-col sm:flex-row gap-2 sm:justify-between border-t border-white/5 pt-4">
          <span>Curcompass – AI Career Copilot · v1.0</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/terms" className="hover:text-zinc-300" data-testid="app-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-zinc-300" data-testid="app-footer-privacy">Privacy</Link>
            <Link to="/refund-policy" className="hover:text-zinc-300" data-testid="app-footer-refund">Refunds</Link>
            <Link to="/contact" className="hover:text-zinc-300" data-testid="app-footer-contact">Contact</Link>
          </span>
        </footer>
        <InstallPrompt />
      </main>
    </div>
  );
}
